import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import { Box, InputAdornment } from "@mui/material";
import { type FC, type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditTimePopover } from "../../../../components/EditTimePopover";
import { SystemValidationTextField } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { ICON_SIZE, INK } from "../../../../theme/tokens";
import { formatShiftClockTime } from "../../../../utils/shiftPeriod";
import type { LocationHoursFieldProps } from "./LocationHoursField";

/** The keys that open the picker from the focused field, as a select's trigger answers them. */
const OPEN_KEYS = new Set(["Enter", " ", "ArrowDown"]);

/** How the form stores a picked time: 24-hour "HH:mm", which `formatShiftClockTime` reads. */
const STORED_TIME_FORMAT = "HH:mm";

/**
 * The span as the shift card's header shows it: through the same formatter, so the field and the header can't
 * disagree on how a time reads.
 */
const hoursLabel = (times: LocationHoursFieldProps["times"]): string =>
	`${formatShiftClockTime(times.start.toFormat(STORED_TIME_FORMAT))} – ${formatShiftClockTime(times.end.toFormat(STORED_TIME_FORMAT))}`;

/**
 * The v2 hours: a read-only field in the form's row of fields ("Hours", "7:00 AM – 7:00 PM") that opens the
 * From/To picker in `EditTimePopover`'s own MUI Menu, anchored on the field. Same open state and handlers as v1.
 */
export const LocationHoursFieldV2: FC<Omit<Extract<LocationHoursFieldProps, { design: "v2" }>, "design">> = ({
	times,
	open,
	setOpen,
	onTimesChange,
	disabled,
}): ReactElement => {
	const { t } = useTranslation();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const hasTimes = times.start.isValid && times.end.isValid;
	const openFrom = (field: HTMLElement): void => {
		if (disabled) return;
		setAnchorEl(field);
		setOpen(true);
	};

	return (
		<Box data-testid="location-hours-field" sx={{ minWidth: 0 }}>
			<SystemValidationTextField
				label={t("hours")}
				value={hasTimes ? hoursLabel(times) : ""}
				placeholder={t("schedule.template.v3.editor.setHours")}
				fullWidth
				disabled={disabled}
				reserveHeightForHelperText={false}
				inputProps={{
					readOnly: true,
					inputProps: { "aria-haspopup": "dialog", "aria-expanded": open },
					endAdornment: (
						<InputAdornment position="end">
							<AccessTimeRoundedIcon aria-hidden sx={(theme) => ({ fontSize: ICON_SIZE.CONTROL, color: open ? theme.palette.primary.main : INK.MUTED })} />
						</InputAdornment>
					),
				}}
				injectedProps={{ onClick: (event) => openFrom(event.currentTarget) }}
				onKeyDown={(event) => {
					if (!OPEN_KEYS.has(event.key)) return;
					event.preventDefault();
					openFrom(event.currentTarget);
				}}
				inputSx={{ cursor: disabled ? "default" : "pointer", "& input": { cursor: disabled ? "default" : "pointer" } }}
				dataTestId="location-hours-input"
			/>
			{open && anchorEl && (
				<EditTimePopover
					anchorEl={anchorEl}
					isActual={false}
					startTime={times.start}
					endTime={times.end}
					onTimeChange={(values) => {
						onTimesChange({
							shiftStartTime: values.newStartTime.toFormat(STORED_TIME_FORMAT),
							shiftEndTime: values.newEndTime.toFormat(STORED_TIME_FORMAT),
						});
						setOpen(false);
					}}
					onClose={(event) => {
						event.stopPropagation();
						setOpen(false);
					}}
				/>
			)}
		</Box>
	);
};
